import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, User, LogOut, LayoutDashboard, ClipboardList, Menu, X, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { useSiteContent, useSiteContentLoaded } from "@/lib/siteContent";
import LangCurrencySwitcher from "@/components/LangCurrencySwitcher";
import { resolveImageUrl } from "@/lib/api";
import PromoStrip from "@/components/PromoStrip";

export default function Navbar({ transparent = false }) {
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [userMenu, setUserMenu] = useState(false);
    const { user, logout } = useAuth() || {};
    const { t } = useTranslation();
    const site = useSiteContent();
    const siteLoaded = useSiteContentLoaded();
    const brand = site?.brand || {};
    const menu = (site?.header_menu || []).filter((m) => m.active).sort((a, b) => (a.order || 0) - (b.order || 0));
    const navigate = useNavigate();

    const toggleDark = () => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle("dark", next);
    };

    const onLogout = () => {
        logout?.();
        setUserMenu(false);
        navigate("/");
    };

    const baseClasses = transparent
        ? "absolute top-0 left-0 right-0 z-30"
        : "sticky top-0 z-30 bg-white/85 dark:bg-slate-900/85 backdrop-blur border-b border-slate-200 dark:border-slate-800";

    const textColor = transparent ? "text-white" : "text-slate-900 dark:text-white";

    return (
        <>
            {!transparent && <PromoStrip />}
            <header className={baseClasses} data-testid="navbar">
            <div className="container mx-auto pl-2 pr-4 md:pl-4 md:pr-6 max-w-7xl">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
                        {brand.logo_url ? (
                            <img
                                src={resolveImageUrl((transparent && brand.logo_dark_url) ? brand.logo_dark_url : brand.logo_url)}
                                alt={brand.name || "Logo"}
                                className="h-11 md:h-14 max-h-14 w-auto object-contain"
                            />
                        ) : siteLoaded ? (
                            <>
                                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center font-bold text-white">{(brand.name || "m")[0].toLowerCase()}</div>
                                <span className={`font-display text-xl font-bold tracking-tight ${textColor}`}>
                                    {brand.name || "Msonline Travel"}
                                </span>
                            </>
                        ) : (
                            <div className="h-12 w-32" />
                        )}
                    </Link>

                    <nav className="hidden md:flex items-center gap-8">
                        {menu.length > 0 ? menu.map((m) => {
                            const kids = (m.children || []).filter((c) => c.active !== false);
                            const hasKids = kids.length > 0;
                            if (!hasKids) {
                                return m.url.startsWith("http") ? (
                                    <a key={m.id} data-testid={`nav-menu-${m.id}`} href={m.url} target={m.target || "_self"} rel="noreferrer" className={`font-medium hover:text-sky-500 transition-colors ${textColor}`}>{m.label}</a>
                                ) : (
                                    <Link key={m.id} data-testid={`nav-menu-${m.id}`} to={m.url} target={m.target || "_self"} className={`font-medium hover:text-sky-500 transition-colors ${textColor}`}>{m.label}</Link>
                                );
                            }
                            // Dropdown (CSS hover-based)
                            return (
                                <div key={m.id} className="relative group" data-testid={`nav-dd-${m.id}`}>
                                    <button type="button" className={`font-medium hover:text-sky-500 transition-colors inline-flex items-center gap-1 ${textColor}`}>
                                        {m.label}
                                        <svg className="h-3.5 w-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-40 min-w-[220px]">
                                        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl py-2">
                                            {kids.map((c) => (
                                                c.url.startsWith("http") ? (
                                                    <a key={c.id} href={c.url} target={c.target || "_self"} rel="noreferrer" data-testid={`nav-child-${c.id}`} className="block px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-sky-50 dark:hover:bg-sky-950/30 hover:text-sky-600">{c.label}</a>
                                                ) : (
                                                    <Link key={c.id} to={c.url} target={c.target || "_self"} data-testid={`nav-child-${c.id}`} className="block px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-sky-50 dark:hover:bg-sky-950/30 hover:text-sky-600">{c.label}</Link>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        }) : (
                            <>
                                <Link data-testid="nav-link-hotels" to="/search" className={`font-medium hover:text-sky-500 transition-colors ${textColor}`}>{t("nav.search")}</Link>
                                <Link data-testid="nav-link-destinations" to="/#destinations" className={`font-medium hover:text-sky-500 transition-colors ${textColor}`}>{t("home.popular_destinations")}</Link>
                                <Link data-testid="nav-link-deals" to="/#deals" className={`font-medium hover:text-sky-500 transition-colors ${textColor}`}>{t("home.deals")}</Link>
                            </>
                        )}
                    </nav>

                    <div className="flex items-center gap-2">
                        <LangCurrencySwitcher light={transparent} />
                        <button
                            data-testid="dark-mode-toggle"
                            onClick={toggleDark}
                            className={`p-2 rounded-full hover:bg-white/10 ${textColor}`}
                            aria-label="Tema değiştir"
                        >
                            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </button>

                        {user ? (
                            <div className="relative">
                                <button
                                    data-testid="user-menu-trigger"
                                    onClick={() => setUserMenu((v) => !v)}
                                    className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium ${transparent ? "bg-white/10 backdrop-blur border-white/20" : ""} ${textColor}`}
                                >
                                    <User className="h-4 w-4" />
                                    <span className="text-sm">{user.full_name?.split(" ")[0]}</span>
                                </button>
                                {userMenu && (
                                    <div data-testid="user-menu" className="absolute right-0 mt-2 w-56 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg overflow-hidden">
                                        <div className="p-3 border-b border-slate-100 dark:border-slate-800">
                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">{user.full_name}</div>
                                            <div className="text-xs text-slate-500">{user.email}</div>
                                        </div>
                                        <Link data-testid="menu-my-bookings" to="/my-bookings" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
                                            <ClipboardList className="h-4 w-4" /> {t("nav.my_bookings")}
                                        </Link>
                                        {user.role === "admin" && (
                                            <Link data-testid="menu-admin" to="/admin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                <LayoutDashboard className="h-4 w-4" /> {t("nav.admin")}
                                            </Link>
                                        )}
                                        <button data-testid="menu-logout" onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30">
                                            <LogOut className="h-4 w-4" /> {t("nav.logout")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="hidden md:flex items-center gap-2">
                                <Link data-testid="nav-list-property" to="/tesisimi-kaydet" className="text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-md shadow-emerald-500/20 transition-all inline-flex items-center gap-1.5">
                                    <Building2 className="h-4 w-4" /> Tesisimi Kaydet
                                </Link>
                                <Link data-testid="nav-login" to="/login" className={`text-sm font-medium px-4 py-2 rounded-full hover:bg-white/10 ${textColor}`}>{t("nav.login")}</Link>
                                <Link data-testid="nav-register" to="/register" className="text-sm font-semibold px-4 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors">{t("nav.register")}</Link>
                            </div>
                        )}

                        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((v) => !v)} className={`md:hidden p-2 rounded-full ${textColor}`}>
                            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {mobileOpen && (
                <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-4 space-y-2">
                    <Link to="/search" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Oteller</Link>
                    <Link to="/#destinations" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Destinasyonlar</Link>
                    <Link to="/#deals" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Fırsatlar</Link>
                    {user ? (
                        <>
                            <Link to="/my-bookings" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Rezervasyonlarım</Link>
                            {user.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 font-medium">Admin Panel</Link>}
                            <button onClick={() => { onLogout(); setMobileOpen(false); }} className="block py-2 text-red-600 font-medium">Çıkış</button>
                        </>
                    ) : (
                        <div className="flex gap-2 pt-2">
                            <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2 rounded-full border border-slate-300">Giriş</Link>
                            <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2 rounded-full bg-sky-500 text-white">Üye Ol</Link>
                        </div>
                    )}
                </div>
            )}
        </header>
        </>
    );
}
